import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';

/**
 * ProvenanceChip — origin marker on every register row (E04-F07).
 *
 * README §6: load-bearing primitive. Marks whether a register entry came
 * from AI (proposed → confirmed pathway), was edited by a consultant after
 * AI proposed it, or was authored manually.
 *
 * Origin enum vs. backend
 * -----------------------
 * The Origin values exposed by this component intentionally model the
 * *UI-facing* provenance vocabulary defined in ticket T-0094, NOT the raw
 * `register_entries.origin` Postgres CHECK values. The DB CHECK lives at
 * db/prisma/schema.prisma → `model RegisterEntry`:
 *   ai_extracted_confirmed_as_is | ai_extracted_edited_by_consultant
 *   | manually_authored
 * The ticket prompt asks for these UI labels:
 *   ai_proposed | ai_confirmed | consultant_edited | manual
 * Callers are responsible for translating DB → UI (`ai_extracted_…
 * confirmed_as_is` ↔ `ai_confirmed`, `…edited_by_consultant` ↔
 * `consultant_edited`, `manually_authored` ↔ `manual`, and `ai_proposed`
 * for entries that have not yet been confirmed). This keeps the chip's
 * public surface aligned with the design contract the ticket settled on,
 * while leaving the storage shape untouched.
 *
 * Tone mapping
 * ------------
 * Matches `prototype/ui.js → provenanceChip()` for the three values the
 * prototype renders (ai_proposed, ai_confirmed, consultant_edited all use
 * `.chip-provenance` — ink-100 bg with ink-700 fg). The fourth value
 * (`manual`) gets a paper-100 background to lean visually warmer — this
 * matches the `.origin-manual` treatment in round5.css for the parallel
 * origin marker.
 *
 * Lineage tooltip
 * ---------------
 * The ticket says: "the chip shows the FULL provenance lineage, not just
 * current state, when hovered. Add a `<Tooltip>` showing 'Confirmed by
 * Sarah Chen on 2026-05-20 (was AI-proposed)'." We use the native `title`
 * attribute (matching prototype/ui.js patterns like `originChip`,
 * `verifyChip`, `citeChip` which all use `title="…"` for hover trails)
 * rather than a Radix tooltip — the design system doesn't ship a tooltip
 * primitive yet and the native browser tooltip is what every other chip
 * in the prototype uses. Swap to Radix later without changing the prop
 * surface.
 *
 * `sharepoint` (E18-F01 / T-0211) marks a document the connected SharePoint
 * folder took; the Documents tab wraps it in a link to the file. Same
 * ink-100 tone as the other system-sourced origins.
 *
 * `ai_rewritten` (E14-F04 / T-0271) marks report text a consultant had the
 * AI rewrite and accepted; the report editor shows it beside
 * `consultant_edited`. Same tone: a person chose the words either way.
 */
export type Origin =
  | 'ai_proposed'
  | 'ai_confirmed'
  | 'consultant_edited'
  | 'ai_rewritten'
  | 'manual'
  | 'sharepoint';

export interface ProvenanceLineage {
  /** Display name of the consultant who confirmed/edited the entry. */
  confirmed_by?: string;
  /** ISO date string (YYYY-MM-DD) when the transition happened. */
  confirmed_at?: string;
  /** The previous origin — used to render the "was AI-proposed" suffix. */
  was?: Origin;
}

const ORIGIN_LABEL: Record<Origin, string> = {
  ai_proposed: 'AI-proposed',
  ai_confirmed: 'AI-confirmed',
  consultant_edited: 'Consultant-edited',
  ai_rewritten: 'AI-rewritten',
  manual: 'Manual',
  sharepoint: 'SharePoint',
};

/**
 * Chip-pill base. Same geometry as VerbChip/posture chip (.chip in
 * tokens.css). Tone variants follow the prototype's
 * `.chip-provenance` rule for the AI-related origins; `manual` lifts to
 * paper-100 so authored-by-consultant rows are visually distinct from
 * the AI-touched ones at a glance.
 */
const provenanceChipVariants = cva(
  'inline-flex h-[22px] items-center whitespace-nowrap rounded-full px-2.5 text-[12px] font-medium leading-[18px]',
  {
    variants: {
      origin: {
        ai_proposed: 'bg-ink-100 text-ink-700',
        ai_confirmed: 'bg-ink-100 text-ink-700',
        consultant_edited: 'bg-ink-100 text-ink-700',
        ai_rewritten: 'bg-ink-100 text-ink-700',
        manual: 'bg-paper-100 text-ink-700 border border-ink-100',
        sharepoint: 'bg-ink-100 text-ink-700',
      } satisfies Record<Origin, string>,
    },
  },
);

type ProvenanceChipVariantProps = VariantProps<typeof provenanceChipVariants>;

/**
 * Format a lineage object into the tooltip string the ticket specifies:
 *   "Confirmed by Sarah Chen on 20 May 2026 (was AI-proposed)"
 * Falls back gracefully when fields are missing — confirmed_by alone, or
 * confirmed_at alone, still produces a useful sentence. Exported so unit
 * tests and downstream callers can re-use the formatting rule without
 * pulling in the React component.
 */
export function formatProvenanceLineage(origin: Origin, lineage?: ProvenanceLineage): string {
  if (!lineage) return ORIGIN_LABEL[origin];
  const action =
    origin === 'consultant_edited'
      ? 'Edited'
      : origin === 'ai_rewritten'
        ? 'Rewritten'
        : 'Confirmed';
  const by = lineage.confirmed_by ? ` by ${lineage.confirmed_by}` : '';
  // Callers pass an ISO date or timestamp; the tooltip uses the app's one
  // date format (C2-45).
  const on = lineage.confirmed_at ? ` on ${formatDate(lineage.confirmed_at)}` : '';
  const was = lineage.was ? ` (was ${ORIGIN_LABEL[lineage.was]})` : '';
  // If we have no lineage detail at all, fall back to the plain label.
  if (!by && !on && !was) return ORIGIN_LABEL[origin];
  return `${action}${by}${on}${was}`;
}

export interface ProvenanceChipProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  'children' | 'title'
> {
  origin: Origin;
  /**
   * Optional provenance lineage. When provided, rendered as a native
   * `title` tooltip on hover (e.g., "Confirmed by Sarah Chen on 20 May 2026
   * (was AI-proposed)"). Omit for chips that show only current state.
   */
  lineage?: ProvenanceLineage;
}

const ProvenanceChip = React.forwardRef<HTMLSpanElement, ProvenanceChipProps>(
  ({ origin, lineage, className, ...props }, ref) => {
    const tooltip = formatProvenanceLineage(origin, lineage);
    const variantOrigin = origin satisfies NonNullable<ProvenanceChipVariantProps['origin']>;
    return (
      <span
        ref={ref}
        className={cn(provenanceChipVariants({ origin: variantOrigin }), className)}
        title={tooltip}
        {...props}
      >
        {ORIGIN_LABEL[origin]}
      </span>
    );
  },
);
ProvenanceChip.displayName = 'ProvenanceChip';

export { ProvenanceChip, ORIGIN_LABEL };
